import { FormControl, Grid, MenuItem, Select } from "@mui/material";
import React, { useRef, useState } from "react";
import { CSVLink } from "react-csv";
import Excel from "exceljs";
import { saveAs } from "file-saver";

import { AuthDetails } from "../../model";
import { Http } from "../../utils";
import { withSnackbar } from "../../utils/common/SnackbarHoc";
import { ec2HostURL } from "../../utils/config";
import images from "../../global-styles/images";

const columns = [
  { header: "Ticket ID", key: "ticketId" },
  { header: "Subject", key: "subject" },
  { header: "Sender Email", key: "email" },
  { header: "Status", key: "status" },
  { header: "Assigned to", key: "assignedTo" },
  { header: "Created Date", key: "createdDate" },
  { header: "Last Modified Date", key: "lastModifiedDate" },
  { header: "Closed Date", key: "closedDate" },
  { header: "Source", key: "source" },
  { header: "Platform Name", key: "platformName" },
  { header: "Account Name", key: "productName" },
  { header: "Account ID and Version", key: "productId" },
];
const csvColumns = [
  { label: "Ticket ID", key: "ticketId" },
  { label: "Subject", key: "subject" },
  { label: "Sender Email", key: "email" },
  { label: "Status", key: "status" },
  { label: "Assigned to", key: "assignedTo" },
  { label: "Created Date", key: "createdDate" },
  { label: "Last Modified Date", key: "lastModifiedDate" },
  { label: "Closed Date", key: "closedDate" },
  { label: "Source", key: "source" },
  { label: "Platform Name", key: "platformName" },
  { label: "Account Name", key: "productName" },
  { label: "Account ID and Version", key: "productId" },
];

interface IDownloadTicketsButton {
  userInfo: AuthDetails;
  snackbarShowMessage: any;
  setIsLoading: any;
  proVerUrl: string;
  startDate: any;
  endDate: any;
  ticketData: any;
}

const DownloadTicketsButton = ({
  userInfo,
  snackbarShowMessage,
  setIsLoading,
  proVerUrl,
  startDate = "",
  endDate = "",
  ticketData = { total: 0 },
}: IDownloadTicketsButton) => {
  const [csvData, setCsvData] = useState<any>([]);
  const csvLinkRef = useRef<any>(null);
  const workbook = new Excel.Workbook();

  const getExportFileName = () => {
    const currentTime = new Date().toISOString();
    const withoutMilliSeconds = currentTime.replace(/\.\d+Z$/, "");
    const fileName = "Tickets_";
    // console.log(`${fileName}${withoutMilliSeconds}.xlsx`, "fileName");
    return `${fileName}${withoutMilliSeconds}.csv`;
  };

  const fetchExportDataInBatches = async () => {
    const dateFilter = `&startDate=${startDate}&endDate=${endDate}`;
    let hasMoreData = true;
    let skip = 0;
    const limit = 10000;
    let ticketDataForExport: any = [];

    // Get token
    const { token, orgId }: any = await Http.get({
      url: `/api/v2/ticketMetrics/ExportTicket?${proVerUrl}${dateFilter}`,
      state: userInfo,
    });

    if (!token) {
      snackbarShowMessage("Something went wrong!", "error", null);
      return;
    }

    const params = new URLSearchParams(proVerUrl);
    const prodId = params.getAll("prodId");
    const prodVersion = params.getAll("prodVersion");
    let index = 0;

    while (hasMoreData) {
      try {
        const response: any = await fetch(
          `${ec2HostURL}/api/exportTicket?token=${token}&orgId=${orgId}&prodId=${prodId[index]}&prodVersion=${prodVersion[index]}&skip=${skip}&limit=${limit}`
        );
        const tickets = await response.json();

        if (tickets?.length === limit) {
          skip = skip + limit;
        } else {
          if (index < prodId.length - 1) {
            index++;
            skip = 0;
          } else {
            hasMoreData = false;
          }
        }

        if (tickets?.length) {
          ticketDataForExport = [...ticketDataForExport, ...tickets];
        }
      } catch (error) {
        snackbarShowMessage("Something went wrong!", "error", null);
        hasMoreData = false;
        return;
      }
    }

    return ticketDataForExport;
  };

  const saveAsExcel = async () => {
    setIsLoading(true);
    const ticketDataForExport = await fetchExportDataInBatches();

    if (!ticketDataForExport?.length) {
      return setIsLoading(false);
    }

    // Format data
    ticketDataForExport.forEach((ticket: any) => {
      return { ...ticket };
    });

    try {
      // const fileName = EXPORT_NAME;
      // creating one worksheet in workbook
      const worksheet = workbook.addWorksheet("Worksheet-1");
      // add worksheet columns
      // each columns contains header and its mapping key from data
      worksheet.columns = columns;
      // updated the font for first row.
      worksheet.getRow(1).font = { bold: true };
      // loop through all of the columns and set the alignment and width.
      worksheet.columns.forEach((column) => {
        column.width = 45;
        column.alignment = { horizontal: "left" };
      });
      // loop through data and add each one to worksheet
      // ticketDataForExport.forEach((singleData: any) => {
      //   worksheet.addRow(singleData);
      // });
      worksheet.addRows(ticketDataForExport);

      // loop through all of the rows and set the outline style.
      worksheet.eachRow({ includeEmpty: false }, (row: Excel.Row) => {
        // loop through the non-empty cells in each row to apply border
        row.eachCell((cell) => {
          // apply border
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      // write the content using writeBuffer
      const buf = await workbook.xlsx.writeBuffer();
      // download the processed file
      const currentTime = new Date().toISOString();
      const withoutMilliSeconds = currentTime.replace(/\.\d+Z$/, "");
      const fileName = "Tickets_";
      saveAs(new Blob([buf]), `${fileName}${withoutMilliSeconds}.xlsx`);
    } catch (error) {
    } finally {
      // removing worksheet's instance to create new one
      workbook.removeWorksheet("Worksheet-1");
      setIsLoading(false);
    }
  };

  const saveAsCSV = async () => {
    setIsLoading(true);
    const ticketDataForExport = await fetchExportDataInBatches();

    if (!ticketDataForExport?.length) {
      return setIsLoading(false);
    }

    setCsvData(ticketDataForExport);
    setTimeout(() => {
      csvLinkRef.current.link.click();
    }, 100);
    setIsLoading(false);
  };

  const saveAsJSON = async () => {
    try {
      setIsLoading(true);
      let ticketDataForExport = await fetchExportDataInBatches();

      if (!ticketDataForExport?.length) {
        return setIsLoading(false);
      }

      // Format data
      ticketDataForExport = ticketDataForExport.map((ticket: any) => ({
        "Ticket ID": ticket.ticketId,
        "Subject": ticket.subject,
        "Sender Email": ticket.email,
        "Status": ticket.status,
        "Assigned to": ticket.assignedTo,
        "Created Date": ticket.createdDate,
        "Last Modified Date": ticket.lastModifiedDate,
        "Closed Date": ticket.closedDate,
        "Source": ticket.source,
        "Platform Name": ticket.platformName,
        "Account Name": ticket.productName,
        "Account ID and Version": ticket.productId,
      }));

      const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
        JSON.stringify(ticketDataForExport)
      )}`;
      const link = document.createElement("a");
      link.href = jsonString;

      const timestampWithoutMS = new Date()
        .toISOString()
        .replace(/\.\d+Z$/, "");
      link.download = `Tickets_${timestampWithoutMS}.json`;

      // Remove the link from the DOM after the download is completed
      link.addEventListener("click", () => {
        link.remove();
      });

      link.click();
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: any) => {
    if (ticketData.total > 100000) {
      const errorMessage =
        "Too many records to export. Please select a narrower date range to keep the Total Tickets less than 100k";
      snackbarShowMessage(errorMessage, "error", null);
      return;
    }

    snackbarShowMessage(
      "Please wait while we export your data. This might take a few minutes!",
      "success",
      5000
    );

    const value = e.target.value;
    if (value == "xls") {
      void saveAsExcel();
    } else if (value == "CSV") {
      void saveAsCSV();
    } else {
      void saveAsJSON();
    }
  };

  return (
    <Grid item className="export-chat-select-btn" spacing={1} pl={1}>
      <CSVLink
        data={csvData}
        headers={csvColumns}
        asyncOnClick={true}
        filename={getExportFileName()}
        ref={csvLinkRef}
      ></CSVLink>
      <FormControl sx={{ minWidth: 130 }} size="small">
        <Select
          disabled={!ticketData.total}
          className="export-chat-select"
          style={{
            background: "#0157ff",
            color: "#fff",
            opacity: ticketData.total ? 1 : 0.5,
          }}
          sx={{
            textAlign: "center",
            fontSize: "13px",
            ".MuiOutlinedInput-notchedOutline": {
              border: 1,
              borderColor: "#0157ff",
              color: "#fff",
            },
            "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              border: 1,
              borderColor: "#0157ff",
              color: "#fff",
            },
            "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                border: 1,
                borderColor: "#0157ff",
                color: "#fff",
              },
            ".MuiSvgIcon-root ": {
              fill: "white !important",
            },
          }}
          displayEmpty
          inputProps={{
            "aria-label": "Without label",
            MenuProps: {
              PaperProps: {
                sx: {},
              },
            },
          }}
          value="Export Ticket"
          onChange={handleInputChange}
        >
          <MenuItem
            key={1}
            value="Export Ticket"
            sx={{ display: "none", justifyContent: "center" }}
          >
            Export Ticket
          </MenuItem>
          <MenuItem key={2} value={"xls"}>
            <img
              style={{
                height: "15px",
                width: "15px",
                marginRight: "5px",
              }}
              src={images.xlsxsvg}
            />
            Export as xlsx
          </MenuItem>
          <MenuItem key={3} value={"CSV"}>
            <img
              style={{
                height: "15px",
                width: "15px",
                marginRight: "5px",
              }}
              src={images.csvsvg}
            />
            Export as csv
          </MenuItem>
          <MenuItem key={4} value={"JSON"}>
            <img
              style={{
                height: "15px",
                width: "15px",
                marginRight: "5px",
              }}
              src={images.json}
            />
            Export as json
          </MenuItem>
        </Select>
      </FormControl>
    </Grid>
  );
};

export default withSnackbar(DownloadTicketsButton);
